import React from "react";
import { parseReferenceNumber } from "../server/reference-number.js";
import { ProgramHeader } from "./ProgramHeader";

// Channel B — the public self-service request form.
//
// Collects the reference number AND the applicant's first name AND last
// initial. Both must match server-side before any link is sent: reference
// numbers are sequential and guessable, so requiring the name is what makes a
// guessed number worthless on its own.
//
// TWO RULES THIS COMPONENT MUST NEVER BREAK:
//
//   1. The response is IDENTICAL whether the reference was wrong, the name was
//      wrong, the reference is rate-limited, or a link really was sent. This
//      component therefore renders whatever `message` the server returns and
//      never inspects it, never branches on it, and never adds a status of its
//      own. Anything that distinguishes the cases — including a different
//      colour or icon — would undo the gate.
//
//   2. Client-side validation covers SHAPE ONLY (is this field filled in, does
//      it look like a reference number). Shape is public knowledge: the format
//      is printed on the sponsor copy. It reveals nothing about whether a
//      particular application exists, which is the only thing worth hiding.
//
// The parser is imported from the server module deliberately: it has zero
// imports of its own, and sharing it means the PDF-dash folding that makes the
// primary path work is identical on both sides rather than reimplemented here.

const SUPPORT_HINT =
  "Don't have the printed sponsor copy, or not sure of the spelling? Email your " +
  "program's director and they can confirm the applicant's first name and last " +
  "initial, or send you a claim link directly.";

interface Errors {
  referenceNumber?: string;
  firstName?: string;
  lastInitial?: string;
  email?: string;
}

export function RequestForm(): React.ReactElement {
  const [referenceNumber, setReferenceNumber] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastInitial, setLastInitial] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [errors, setErrors] = React.useState<Errors>({});
  const [busy, setBusy] = React.useState(false);
  const [acknowledgement, setAcknowledgement] = React.useState("");

  // Shown once the reference parses, so someone who pasted an en-dashed value
  // out of the PDF can SEE it was understood. Purely local — no server call.
  const parsed = parseReferenceNumber(referenceNumber);
  const normalizedHint =
    parsed && parsed.normalized !== referenceNumber.trim().toUpperCase()
      ? parsed.normalized
      : "";

  function validate(): Errors {
    const next: Errors = {};
    if (!referenceNumber.trim()) next.referenceNumber = "Enter the reference number.";
    else if (!parsed)
      next.referenceNumber = "That doesn't look like a reference number (e.g. MDBS-2027-0347).";
    if (!firstName.trim()) next.firstName = "Enter the applicant's first name.";
    if (!lastInitial.trim()) next.lastInitial = "Enter the first letter of their last name.";
    else if (!/[A-Za-z]/.test(lastInitial)) next.lastInitial = "Use a single letter.";
    if (!email.trim()) next.email = "Enter your email address.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()))
      next.email = "That doesn't look like an email address.";
    return next;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;

    setBusy(true);
    try {
      const res = await fetch("/api/claim-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          referenceNumber: parsed?.normalized ?? referenceNumber,
          firstName,
          lastInitial,
          email,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      // Rendered verbatim, never inspected. The rate-limit response carries its
      // text under `error`, and it is the same sentence by design.
      setAcknowledgement(
        body.message ??
          body.error ??
          "If the information you entered matches an application, a claim link is on its way."
      );
    } catch {
      // A network failure is the ONE thing that is genuinely not about whether
      // the details matched, and saying so reveals nothing about the applicant.
      setAcknowledgement("");
      setErrors({ email: "Could not reach the server. Please try again." });
    } finally {
      setBusy(false);
    }
  }

  if (acknowledgement) {
    return (
      <><ProgramHeader />
      <main className="mx-auto max-w-xl px-4 py-10">
        <div className="section-card">
          <h1 className="text-xl font-semibold text-program">Check your email</h1>
          <p className="mt-3 text-sm text-slate-700">{acknowledgement}</p>
          <p className="field-hint mt-4">{SUPPORT_HINT}</p>
          <button
            type="button"
            className="btn-secondary mt-6"
            onClick={() => {
              setAcknowledgement("");
              setErrors({});
            }}
          >
            Make another request
          </button>
        </div>
      </main></>
    );
  }

  return (
    <><ProgramHeader />
    <main className="mx-auto max-w-xl px-4 py-10">
      <div className="section-card">
        <h1 className="text-xl font-semibold text-program">Claim your sponsored student</h1>
        <p className="mt-2 text-sm text-slate-700">
          Maryland Boys State &amp; Maryland Girls State. Enter the details from the
          printed sponsor copy the family gave you, and we'll email you a link to
          complete your claim.
        </p>

        <form className="mt-6" onSubmit={submit} noValidate>
          <div className="mb-4">
            <label className="field-label" htmlFor="ref">
              Reference number
            </label>
            <input
              id="ref"
              className="field-input font-mono"
              placeholder="MDBS-2027-0347"
              autoComplete="off"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
            {normalizedHint && <p className="field-hint">Reading this as {normalizedHint}</p>}
            {errors.referenceNumber && <p className="field-error">{errors.referenceNumber}</p>}
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
            <div>
              <label className="field-label" htmlFor="first">
                Applicant's first name
              </label>
              <input
                id="first"
                className="field-input"
                autoComplete="off"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              {errors.firstName && <p className="field-error">{errors.firstName}</p>}
            </div>
            <div>
              <label className="field-label" htmlFor="initial">
                Last initial
              </label>
              <input
                id="initial"
                className="field-input"
                maxLength={2}
                autoComplete="off"
                placeholder="S"
                value={lastInitial}
                onChange={(e) => setLastInitial(e.target.value)}
              />
              {errors.lastInitial && <p className="field-error">{errors.lastInitial}</p>}
            </div>
          </div>

          {/* Mitigation for the identical-response rule: tell people WHERE to
              read the name from, so a mismatch is unlikely in the first place. */}
          <p className="field-hint -mt-2 mb-4">
            Enter the applicant's first name exactly as it appears on the sponsor copy
            they gave you.
          </p>

          <div className="mb-6">
            <label className="field-label" htmlFor="email">
              Your email address
            </label>
            <input
              id="email"
              className="field-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && <p className="field-error">{errors.email}</p>}
          </div>

          <button className="btn-primary w-full" type="submit" disabled={busy}>
            {busy ? "Sending…" : "Email me a claim link"}
          </button>
        </form>
      </div>

      <div className="section-card">
        <h2 className="text-sm font-semibold text-slate-800">Other ways to claim</h2>
        <p className="mt-2 text-sm text-slate-700">{SUPPORT_HINT}</p>
        <ul className="mt-3 space-y-1 text-sm text-slate-700">
          <li>
            <strong>Maryland Boys State:</strong> boysstate@mdlegion.org
          </li>
          <li>
            <strong>Maryland Girls State:</strong> alamdgs@gmail.com
          </li>
        </ul>
        <p className="field-hint mt-3">
          Email is fastest. If you'd rather call, contact your Department headquarters
          and they can put you through.
        </p>
      </div>
    </main></>
  );
}
